import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface AccessibilitySettings {
  fontSize: 'normal' | 'large' | 'extra-large';
  highContrast: boolean;
  reducedMotion: boolean;
  screenReaderMode: boolean;
  keyboardNavigation: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSetting: <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => void;
  resetSettings: () => void;
  announceToScreenReader: (message: string) => void;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 'normal',
  highContrast: false,
  reducedMotion: false,
  screenReaderMode: false,
  keyboardNavigation: true,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    // Load settings from localStorage
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch {
        return defaultSettings;
      }
    }
    
    // Check for system preferences
    const systemSettings = { ...defaultSettings };
    
    // Check for prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      systemSettings.reducedMotion = true;
    }
    
    // Check for prefers-contrast
    if (window.matchMedia('(prefers-contrast: high)').matches) {
      systemSettings.highContrast = true;
    }
    
    return systemSettings;
  });

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // Remove existing accessibility styles
    const existingStyle = document.getElementById('accessibility-dynamic-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Create dynamic CSS based on settings
    const fontScales = {
      normal: 1,
      large: 1.125,
      'extra-large': 1.25
    };
    
    const lineHeights = {
      normal: 1.5,
      large: 1.6,
      'extra-large': 1.7
    };
    
    const scale = fontScales[settings.fontSize];
    const lineHeight = lineHeights[settings.fontSize];
    
    // Create comprehensive CSS that overrides everything
    const dynamicCSS = `
      /* Font Size Scaling - Override all text */
      * {
        font-size: ${scale}rem !important;
        line-height: ${lineHeight} !important;
      }
      
      /* Specific overrides for different elements */
      h1, .text-4xl, .text-3xl { font-size: ${2.25 * scale}rem !important; }
      h2, .text-2xl { font-size: ${1.875 * scale}rem !important; }
      h3, .text-xl { font-size: ${1.5 * scale}rem !important; }
      h4, .text-lg { font-size: ${1.25 * scale}rem !important; }
      h5, .text-base { font-size: ${1 * scale}rem !important; }
      h6, .text-sm { font-size: ${0.875 * scale}rem !important; }
      .text-xs { font-size: ${0.75 * scale}rem !important; }
      
      /* Button and input scaling */
      button, input, textarea, select {
        font-size: ${scale}rem !important;
        line-height: ${lineHeight} !important;
      }
      
      /* High contrast mode */
      ${settings.highContrast ? `
        * {
          background-color: #000000 !important;
          color: #ffffff !important;
          border-color: #ffffff !important;
        }
        
        a, button {
          background-color: #ffff00 !important;
          color: #000000 !important;
        }
        
        input, textarea, select {
          background-color: #ffffff !important;
          color: #000000 !important;
          border: 2px solid #000000 !important;
        }
      ` : ''}
      
      /* Reduced motion */
      ${settings.reducedMotion ? `
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      ` : ''}
      
      /* Screen reader optimizations */
      ${settings.screenReaderMode ? `
        * {
          outline: 2px solid #0066cc !important;
          outline-offset: 2px !important;
        }
        
        *:focus {
          outline: 3px solid #ff6600 !important;
          outline-offset: 2px !important;
        }
      ` : ''}
    `;
    
    // Inject the CSS
    const styleElement = document.createElement('style');
    styleElement.id = 'accessibility-dynamic-styles';
    styleElement.textContent = dynamicCSS;
    document.head.appendChild(styleElement);
        
    // Save to localStorage
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
  }, [settings]);

  // Set up keyboard navigation
  useEffect(() => {
    if (!settings.keyboardNavigation) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip links with Alt + S
      if (event.altKey && event.key === 's') {
        event.preventDefault();
        const skipLink = document.querySelector('[data-skip-link]') as HTMLElement;
        if (skipLink) {
          skipLink.focus();
          skipLink.click();
        }
      }
      
      // Focus management with Tab
      if (event.key === 'Tab') {
        document.body.classList.add('keyboard-navigation');
      }
    };

    const handleMouseDown = () => {
      document.body.classList.remove('keyboard-navigation');
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [settings.keyboardNavigation]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Announce changes to screen readers
    const messages = {
      fontSize: t('accessibility.announcements.fontSizeChanged', { size: value }),
      highContrast: value 
        ? t('accessibility.announcements.highContrastEnabled')
        : t('accessibility.announcements.highContrastDisabled'),
      reducedMotion: value
        ? t('accessibility.announcements.reducedMotionEnabled')
        : t('accessibility.announcements.reducedMotionDisabled'),
      screenReaderMode: value
        ? t('accessibility.announcements.screenReaderModeEnabled')
        : t('accessibility.announcements.screenReaderModeDisabled'),
      keyboardNavigation: value
        ? t('accessibility.announcements.keyboardNavigationEnabled')
        : t('accessibility.announcements.keyboardNavigationDisabled'),
    };
    
    if (messages[key]) {
      announceToScreenReader(messages[key]);
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    announceToScreenReader(t('accessibility.announcements.settingsReset'));
  };

  const announceToScreenReader = (message: string) => {
    // Create a live region for screen reader announcements
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Also show a visual notification for debugging
    const visualNotification = document.createElement('div');
    visualNotification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #2563eb;
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: all 0.3s ease;
    `;
    visualNotification.textContent = message;
    
    document.body.appendChild(visualNotification);
    
    // Remove after announcement
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
      if (document.body.contains(visualNotification)) {
        visualNotification.style.opacity = '0';
        visualNotification.style.transform = 'translateY(-10px)';
        setTimeout(() => {
          if (document.body.contains(visualNotification)) {
            document.body.removeChild(visualNotification);
          }
        }, 300);
      }
    }, 2000);
  };

  const value: AccessibilityContextType = {
    settings,
    updateSetting,
    resetSettings,
    announceToScreenReader,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
} 